/** /line-sets — cross-tenant Vehicle_Route_Set catalog (grid).
 *
 * Columns: name → RouterLink, tenant, route_count, updated_ts. Filters: tenant
 * scope + free-text search. */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, CircularProgress } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as routeSetsApi from '@/api/routeSets';
import type { RouteSetListItem } from '@/api/routeSets';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { formatRelativeTime } from '@/utils/format';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { LineSetCreateDialog } from './LineSetCreateDialog';

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);
const DEFAULT_ORDER = ['id', 'name', 'tenant', 'route_count', 'updated_ts'];

export function LineSetsListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_fleet_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  const presets = useGridPresets({
    grid: 'route_sets',
    defaultOrder: DEFAULT_ORDER,
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what a create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());

  const columns = useMemo<GridColDef<RouteSetListItem>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (
          <RouterLink to={`/line-sets/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'route_count',
        headerName: 'Routes',
        width: 110,
        sortable: false,
        renderCell: (p) => p.row.route_count,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 150,
        sortable: true,
        valueGetter: (_, row) => row.updated_ts ?? '',
        renderCell: (p) => (p.row.updated_ts ? formatRelativeTime(p.row.updated_ts) : '—'),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      routeSetsApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'name',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="bus-stops-line-sets"
        title="Line sets"
        subtitle="Cross-tenant line-set catalog (routes + stops)."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New line set
            </Button>
          )
        }
      />

      <LineSetCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<RouteSetListItem>
          queryKey={['route_sets']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="route_sets"
          batchActions={[deleteBatchAction('console_fleet_manage')]}
          defaultSort={{ field: 'name', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name',
            exportFileName: 'line-sets',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
